import {PartType} from '@dv5/domain'
import React, {FC, useState} from 'react'
import {Icon, IconName} from '../buttons/Icon'
import {Input} from '../inputs/Input'
import {MultilineEditor} from '../inputs/MultilineEditor'
import {MarkdownViewer} from '../markdown/MarkdownViewer'
import {DocumentPartForm} from './documentTemplateForm'

interface Props {
    part: DocumentPartForm
    index: number
    onChange: (part: DocumentPartForm) => void
    onRemove: () => void
    onMoveUp?: () => void
    onMoveDown?: () => void
    onConfirm: () => void
    onCancel: () => void
}

const partTypesTitle: Record<PartType, string> = {
    [PartType.MARKDOWN]: 'Markdown',
    [PartType.PLAINTEXT]: 'Plain Text',
    [PartType.CSV_TABLE]: 'CSV Table',
    [PartType.DIARY]: 'Diary',
    [PartType.SECRET]: 'Secret',
}

export const DocumentTemplatePartForm: FC<Props> = ({
    part,
    index,
    onChange,
    onRemove,
    onMoveUp,
    onMoveDown,
    onConfirm,
    onCancel,
}) => {
    const [contentMode, setContentMode] = useState<'edit' | 'preview'>('edit')

    const changeHeader = (partHeader: string) => {
        onChange({...part, partHeader})
    }
    const changeContent = (partContent: string) => {
        onChange({...part, partContent})
    }
    const changeType = (partType: PartType) => {
        onChange({...part, partType})
    }

    return (
        <div className="document-template-part-form">
            <div className="document-template-part-form__header">
                <span className="document-template-part-form__title">Part {index + 1}</span>
                <div className="document-template-part-form__actions">
                    <button
                        type="button"
                        className="document-template-part-form__action-button"
                        onClick={onMoveUp}
                        disabled={!onMoveUp}
                        title="Move up"
                        aria-label="Move part up"
                    >
                        <Icon name={IconName.Up} width="16" height="16" />
                    </button>
                    <button
                        type="button"
                        className="document-template-part-form__action-button"
                        onClick={onMoveDown}
                        disabled={!onMoveDown}
                        title="Move down"
                        aria-label="Move part down"
                    >
                        <Icon name={IconName.Down} width="16" height="16" />
                    </button>
                    <button
                        type="button"
                        className="document-template-part-form__action-button document-template-part-form__action-button--remove"
                        onClick={onRemove}
                        title="Remove part"
                        aria-label="Remove part"
                    >
                        <Icon name={IconName.Delete} width="16" height="16" />
                    </button>
                </div>
            </div>

            <div className="document-template-part-form__type-buttons">
                {(Object.values(PartType) as PartType[]).map((partType) => (
                    <button
                        key={partType}
                        type="button"
                        onClick={() => changeType(partType)}
                        className={`document-template-part-form__type-button ${
                            part.partType === partType ? 'document-template-part-form__type-button--active' : ''
                        }`}
                        title={partTypesTitle[partType]}
                    >
                        {partTypesTitle[partType]}
                    </button>
                ))}
            </div>

            <Input id={'documentTemplatePartHeader-' + part.id}
                   value={part.partHeader}
                   setValue={changeHeader}
                   label={'Header'}
                   placeholder={'Part header'}
                   onConfirm={onConfirm}
                   onCancel={onCancel}
            />

            <label className="document-template-part-form__label">Content</label>
            <div className="document-template-part-form__content-tabs">
                <button className={`content-tab ${contentMode === 'edit' ? 'active' : ''}`}
                        onClick={() => setContentMode('edit')}>Edit</button>
                <button className={`content-tab ${contentMode === 'preview' ? 'active' : ''}`}
                        onClick={() => setContentMode('preview')}>Preview</button>
            </div>
            {contentMode === 'edit' ? (
                <MultilineEditor
                    value={part.partContent}
                    setValue={changeContent}
                    onConfirm={onConfirm}
                    onCancel={onCancel}
                    settings={{autoResize: true}}
                    disabled={false}
                />
            ) : (
                <MarkdownViewer
                    sourceCode={part.partContent}
                    compactMode={false}
                    onToggleCheckBox={() => {}}
                />
            )}
        </div>
    )
}
